import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import { useSports, useThemesRef } from '@/features/reference/useReference';
import type { Guidance } from '@/api/guidanceService';

interface GuidanceFormPayload {
  themeId: string;
  sportIdentifier: string;
  coachNote: string;
  nutrition: string;
  warmupPattern: string;
  cooldownPattern: string;
}

interface GuidanceModalProps {
  open: boolean;
  onClose: () => void;
  guidance: Guidance | null;
  onCreate: (payload: GuidanceFormPayload) => Promise<unknown>;
  onUpdate: (payload: GuidanceFormPayload) => Promise<unknown>;
  isSaving: boolean;
}

const GuidanceModal = ({ open, onClose, guidance, onCreate, onUpdate, isSaving }: GuidanceModalProps) => {
  const { data: themes = [] } = useThemesRef();
  const { data: sports = [] } = useSports();
  const isEdit = Boolean(guidance);

  const [themeId, setThemeId] = useState('');
  const [sportIdentifier, setSportIdentifier] = useState('');
  const [coachNote, setCoachNote] = useState('');
  const [nutrition, setNutrition] = useState('');
  const [warmupPattern, setWarmupPattern] = useState('');
  const [cooldownPattern, setCooldownPattern] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setThemeId(guidance?.themeId ?? '');
      setSportIdentifier(guidance?.sportIdentifier ?? '');
      setCoachNote(guidance?.coachNote ?? '');
      setNutrition(guidance?.nutrition ?? '');
      setWarmupPattern(guidance?.warmupPattern ?? '');
      setCooldownPattern(guidance?.cooldownPattern ?? '');
    }
  }, [open, guidance]);

  const themeLabel = guidance?.themeLabel ?? themes.find((t) => t.id === themeId)?.label ?? '';
  const sportLabel = sports.find((s) => s.identifier === sportIdentifier)?.label ?? sportIdentifier;

  const handleSubmit = async () => {
    setError('');
    if (!isEdit && (!themeId || !sportIdentifier)) return setError('Thématique et sport sont requis.');
    try {
      const payload = {
        themeId,
        sportIdentifier,
        coachNote: coachNote.trim(),
        nutrition: nutrition.trim(),
        warmupPattern: warmupPattern.trim(),
        cooldownPattern: cooldownPattern.trim(),
      };
      await (isEdit ? onUpdate(payload) : onCreate(payload));
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Modifier le contenu' : 'Ajouter un contenu'}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {isEdit ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-500">Thématique</span>
              <p className="text-gray-900 dark:text-gray-100">{themeLabel}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Sport</span>
              <p className="text-gray-900 dark:text-gray-100">{sportLabel}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <Select label="Thématique" value={themeId} onChange={(e) => setThemeId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
            <Select label="Sport" value={sportIdentifier} onChange={(e) => setSportIdentifier(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {sports.map((s) => (
                <option key={s.identifier} value={s.identifier}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
        )}

        <Textarea label="Mot du coach" rows={4} value={coachNote} onChange={(e) => setCoachNote(e.target.value)} />
        <Textarea label="Nutrition" rows={4} value={nutrition} onChange={(e) => setNutrition(e.target.value)} />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Échauffement (nomenclature)"
            placeholder="ex. 10' @Z2"
            value={warmupPattern}
            onChange={(e) => setWarmupPattern(e.target.value)}
          />
          <Input
            label="Récupération (nomenclature)"
            placeholder="ex. 5' @Z1"
            value={cooldownPattern}
            onChange={(e) => setCooldownPattern(e.target.value)}
          />
        </div>
        <p className="text-xs text-gray-500">Optionnel — les nomenclatures sont validées à l'enregistrement.</p>

        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

export default GuidanceModal;
